import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {DocumentRepository2} from '../../components/DocumentRepository';
import FasesSection from '../../sections/FasesSection';
import Descargas from '../../components/Descargas';
import './SierradelTigre.css';
import {
  ChevronRight,
  ExternalLink,
  Users,
  Map,
  Landmark,
  Calendar,
  MapPin,
  Info,
  MountainSnow,
  FileSignature,
  FileSpreadsheet,
  BarChart3,
  Zap,
  FileText,
  ClockIcon,
  TreePineIcon,
  Book,
  Map as MapIcon,
  Target,
  GlobeIcon,
  Compass,
  DownloadCloud,
  CheckCircle,
  ArrowDown,
  Layers,
  Download,
} from 'lucide-react';

const DetailBadge = ({ icon: Icon, label, value }) => (
  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl transition-all hover:bg-teal-100 dark:hover:bg-teal-900/30">
    <Icon className="text-teal-600 dark:text-teal-400 mr-3 flex-shrink-0" size={20} />
    <div className="text-sm">
      <span className="font-semibold text-gray-700 dark:text-gray-300 block leading-tight">{label}:</span>
      <span className="text-gray-900 dark:text-white font-bold">{value}</span>
    </div>
  </div>
);

const ModernBitacoraSection = ({ items }) => {
  const [expandedSection, setExpandedSection] = useState(0);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Bitácora del Programa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Registro documental completo del proceso de ordenamiento ecológico
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={index}
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-6 rounded-2xl transition-all duration-300 text-left ${
                  expandedSection === index
                    ? `bg-gradient-to-br ${item.color} text-white shadow-2xl`
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl ${
                    expandedSection === index
                      ? 'bg-white/20'
                      : `bg-gradient-to-br ${item.color}`
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                </div>
                <p className={`text-sm ${
                  expandedSection === index ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {expandedSection === index ? 'Ver documentos ↓' : 'Ver documentos →'}
                </p>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence>
          {expandedSection !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                {items[expandedSection].content.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mr-3" />
                      {section.subtitle}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.items.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 
                            dark:hover:from-blue-900/20 dark:hover:to-teal-900/20 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-500 group-hover:text-teal-500 transition-colors" />
                            <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                              {doc.text}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    {items[expandedSection].content.reduce((total, section) => total + section.items.length, 0)} documentos disponibles
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const FullWidthVisorWithSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('uga');

  return (
    <section id="visor-completo" className="py-16">
      <div className="max-w-[2000px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Plataforma Geoespacial Completa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Sistema de información geográfica integrado con documentación técnica completa
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`lg:w-96 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 sticky top-6">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mb-4 w-full p-2 bg-gradient-to-r from-blue-500 to-teal-500 
                  text-white rounded-lg flex items-center justify-center gap-2"
              >
                {sidebarOpen ? 'Ocultar Panel' : 'Mostrar Panel'}
                <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-90' : ''}`} />
              </button>

              <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('uga')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'uga'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Clasificación UGA
                </button>
                <button
                  onClick={() => setActiveTab('descripcion')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'descripcion'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Descripción UGA
                </button>
                <button
                  onClick={() => setActiveTab('politica')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'politica'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Política Ambiental
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {activeTab === 'uga' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Layers className="w-5 h-5 text-blue-500 mr-2" />
                        Clasificación de UGAs
                      </h3>
                      <img
                        src="/images/Clave_UGA_Sierra_Occidental.PNG"
                        alt="Clasificación de Unidades de Gestión Ambiental"
                        className="w-full h-auto rounded-lg shadow-md mb-4"
                      />
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Mapa de clasificación de las Unidades de Gestión Ambiental (UGAs) 
                        de la región Sierra del Tigre.
                      </p>
                    </div>
                  )}

                  {activeTab === 'descripcion' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Info className="w-5 h-5 text-teal-500 mr-2" />
                        Descripción de las UGAs
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Las <strong>Unidades de Gestión Ambiental (UGAs)</strong> son la unidad mínima 
                        del territorio a la que se asignan determinados lineamientos y estrategias 
                        ecológicas para su manejo específico.
                      </p>
                      <div className="space-y-4">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Características</h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                            <li>• Unidad territorial homogénea</li>
                            <li>• Criterios específicos de manejo</li>
                            <li>• Estrategias ecológicas definidas</li>
                            <li>• Monitoreo individualizado</li>
                          </ul>
                        </div>
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Objetivos</h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                            <li>• Gestión ambiental específica</li>
                            <li>• Aplicación de lineamientos</li>
                            <li>• Evaluación de resultados</li>
                            <li>• Adaptación de estrategias</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'politica' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Target className="w-5 h-5 text-purple-500 mr-2" />
                        Política Ambiental
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        El presente <strong>Modelo de Ordenamiento Ecológico y Territorial Regional</strong> considera una zonificación, integrada por seis políticas que buscan definir el estado deseable del territorio mediante lineamientos, estrategias y criterios para conservar y controlar el deterioro del ambiente así como el aprovechamiento sustentable de los recursos naturales.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="https://siga.jalisco.gob.mx/ordenamiento/visores/visorSierradelTigre/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-teal-500 text-white 
                      rounded-lg text-sm font-medium text-center hover:shadow-lg transition-all"
                  >
                    <ExternalLink className="w-4 h-4 inline mr-1" />
                    Abrir en Nueva Ventana
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="flex-1">
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <GlobeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Visor GIS Sierra del Tigre</h3>
                    <p className="text-sm text-gray-400">Sistema interactivo de información geográfica</p>
                  </div>
                </div>
              </div>
              <div className="h-[600px]">
                <iframe
                  src="https://siga.jalisco.gob.mx/ordenamiento/visores/visorSierradelTigre/index.html"
                  className="w-full h-full border-0"
                  title="Visor GIS Sierra del Tigre"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatusBadge = () => {
  return (
    <motion.div 
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 
        backdrop-blur-md border border-amber-500/30"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse mr-2" />
      <span className="text-sm font-medium text-white tracking-wider">PROCESO</span>
      <div className="ml-2 pl-2 border-l border-white/20">
        <span className="text-xs text-white/70">Suspendido</span>
      </div>
    </motion.div>
  );
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const Items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "images/vectoriales.png",
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Descargas/Vectoriales_Tigre.zip",
    target: "_blank",
  },
  {
    title: "Fichas UGAs",
    description: "Informacion detallada de cada Unidad de Gestión Ambiental (UGA).",
    imageUrl: "images/FICHAS_UGA.png",
    baseColor: "green",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Descargas/Fichas_UGAs_Tigre.pdf",
    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio.",
    imageUrl: "images/Criterios.png",
    baseColor: "orange",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Descargas/Criterios_Tigre.pdf",
    target: "_blank",
  },
  {
    title: "Estrategias Ecologicas",
    description: "Proyectos de orientación para la protección del entorno natural.",
    imageUrl: "images/Estrategias.png",
    baseColor: "red",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Descargas/Estrategias_Tigre.pdf",
    target: "_blank",
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los recursos naturales de la región.",
    imageUrl: "images/Lineamientos.png",
    baseColor: "indigo",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Descargas/Lineamientos_Tigre.pdf",
    target: "_blank",
  },
  {
    title: "Usos y Actividades",
    description: "Sujetos a controles ambientales en la región.",
    imageUrl: "images/COMPATIBILIDADES.png",
    baseColor: "teal",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Descargas/Usos_Actividades_Tigre.pdf",
    target: "_blank",
  },
];

const municipalities = [
  {
    id: 1,
    name: 'Mazamitla',
    shield: 'images/8-Sierra_del_Tigre.png',
    description: 'Reconocido como el "Pueblo Mágico" de Jalisco, destaca por su vocación turística y forestal, con extensos bosques de pino y encino que lo convierten en un destino ideal para el ecoturismo y las actividades al aire libre.',
    population: '14,043 habitantes',
    area: '577.1 km²',
    foundation: '1886',
    attractions: 'Bosques de pino-encino, cabañas rústicas, el centro mágico con arquitectura típica, el mirador del Cerro del Chupan y los paisajes del río Mazamitla. Ideal para senderismo, paseos a caballo y turismo de naturaleza.',
    color: 'from-emerald-500 to-teal-600',
    administrativeRegion: 'Región Sureste',
    EcologicaZona: 'Área Natural Protegida Bosque de Mazamitla.',
    NumerodeUgas: '',
  },
  {
    id: 2,
    name: 'La Manzanilla de la Paz',
    shield: 'images/8-Sierra_del_Tigre.png',
    description: 'Municipio de tradición agrícola y forestal, enclavado en la sierra con paisajes de bosques mesófilos y pino-encino. Su entorno natural favorece la conservación de recursos hídricos y la biodiversidad.',
    population: '4,124 habitantes',
    area: '135.5 km²',
    foundation: '1919',
    attractions: 'Áreas boscosas ideales para el ecoturismo, manantiales y arroyos de montaña, y paisajes serranos que ofrecen un entorno de tranquilidad y contacto con la naturaleza.',
    color: 'from-blue-500 to-cyan-600',
    administrativeRegion: 'Región Sureste',
    EcologicaZona: '',
    NumerodeUgas: '',
  },
  {
    id: 3,
    name: 'Concepción de Buenos Aires',
    shield: 'images/8-Sierra_del_Tigre.png',
    description: 'Con una economía basada en la agricultura y ganadería, este municipio serrano cuenta con bosques de pino-encino y paisajes de lomeríos que albergan una importante diversidad de flora y fauna.',
    population: '6,462 habitantes',
    area: '265.8 km²',
    foundation: '1883',
    attractions: 'Bosques de pino-encino, el cerro El Tigre y áreas naturales para la observación de aves y caminatas. Su entorno rural y tradicional ofrece una experiencia auténtica de la vida en la sierra.',
    color: 'from-amber-500 to-orange-600',
    administrativeRegion: 'Región Sureste',
    EcologicaZona: '',
    NumerodeUgas: '',
  },
  {
    id: 4,
    name: 'Valle de Juárez',
    shield: 'images/8-Sierra_del_Tigre.png',
    description: 'Municipio de origen agrícola y forestal, localizado en la región de la Sierra del Tigre. Su territorio presenta un relieve montañoso con bosques templados y zonas de aprovechamiento sustentable.',
    population: '6,682 habitantes',
    area: '195.7 km²',
    foundation: '1883',
    attractions: 'Paisajes serranos, áreas boscosas para la práctica del senderismo y el turismo rural, así como sitios de interés ecológico ideales para la conservación de la biodiversidad local.',
    color: 'from-purple-500 to-indigo-600',
    administrativeRegion: 'Región Sureste',
    EcologicaZona: '',
    NumerodeUgas: '',
  },
  {
    id: 5,
    name: 'Quitupan',
    shield: 'images/8-Sierra_del_Tigre.png',
    description: 'Con vocación agrícola, forestal y ganadera, el municipio se encuentra en la serranía con presencia de bosques templados y paisajes montañosos que favorecen la conservación de ecosistemas.',
    population: '7,719 habitantes',
    area: '351.6 km²',
    foundation: '1870',
    attractions: 'Bosques de niebla y pino-encino, manantiales naturales, y el entorno serrano con caídas de agua y arroyos que enriquecen el paisaje para el turismo de naturaleza.',
    color: 'from-green-500 to-emerald-600',
    administrativeRegion: 'Región Sureste',
    EcologicaZona: '',
    NumerodeUgas: '',
  },
  {
    id: 6,
    name: 'Tamazula de Gordiano',
    shield: 'images/8-Sierra_del_Tigre.png',
    description: 'Importante centro agrícola y ganadero de la región, con presencia de bosques templados y selva baja. Su territorio alberga una significativa biodiversidad y recursos hídricos.',
    population: '37,591 habitantes',
    area: '1,361.3 km²',
    foundation: '1824',
    attractions: 'El río Tamazula, balnearios naturales, zonas boscosas para ecoturismo, y el centro histórico con arquitectura tradicional. Destacan sus paisajes ribereños y áreas de conservación ecológica.',
    color: 'from-blue-800 to-blue-900',
    administrativeRegion: 'Región Sureste',
    EcologicaZona: '',
    NumerodeUgas: '',
  },
];

const bitacoraItems = [
  {
    title: 'FASE DE FORMULACIÓN',
    icon: FileSignature,
    color: 'from-blue-500 to-cyan-500',
    content: [
      {
        subtitle: 'Actas de Aprobación',
        items: [
          { text: 'Acta de aprobación - Valle de Juárez', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/01.-%20Acta%20de%20aprobaci%C3%B3n%20Valle%20de%20Ju%C3%A1rez.pdf' },
          { text: 'Acta de aprobación - Tamazula de Gordiano', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/02.-%20Acta%20de%20aprobaci%C3%B3n%20de%20Tamazula.pdf' },
          { text: 'Acta de aprobación - Mazamitla', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/03.-%20Acta%20de%20aprobaci%C3%B3n%20Mazamitla.pdf' },
          { text: 'Acta de aprobación - La Manzanilla', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/04.-%20Acta%20de%20aprobaci%C3%B3n%20La%20Manzanilla.pdf' },
          { text: 'Acta de aprobación - Concepción de Buenos Aires', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/05.-%20Acta%20de%20aprobaci%C3%B3n%20Concepci%C3%B3n%20de%20Buenos%20Aires.pdf' },
          { text: 'Acta de aprobación - Quitupan', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/06.-%20Acta%20de%20aprobaci%C3%B3n%20Quitupan.pdf' },
          { text: 'Comunicado de suspensión del POETR', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/Reporte%20Siera%20del%20Tigre.docx' },
        ]
      }
    ]
  },
  {
    title: 'FASE DE EXPEDICIÓN',
    icon: FileSpreadsheet,
    color: 'from-emerald-500 to-green-500',
    content: [
      {
        subtitle: 'Documentos',
        items: []
      }
    ]
  },
  {
    title: 'FASE DE EJECUCIÓN',
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    content: [
      {
        subtitle: 'Seguimiento',
        items: []
      }
    ]
  },
  {
    title: 'FASE DE EVALUACIÓN',
    icon: BarChart3,
    color: 'from-purple-500 to-violet-500',
    content: [
      {
        subtitle: 'Evaluaciones',
        items: []
      }
    ]
  }
];

const TigreMain = [
  {
    id: 0,
    title: "Informe de Diagnóstico",
    subtitle: "Estatus del proceso de ordenamiento",
    icon: <Book className="w-5 h-5 text-green-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/Reporte%20Siera%20del%20Tigre.pdf"
  },
];

const ModernBanner = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const stats = [
    { value: '6', label: 'Municipios', icon: MapPin },
    { value: '2,887', label: 'km² aprox.', icon: MapIcon },
    { value: '-', label: 'Inicio', icon: Calendar },
    { value: 'Suspendido', label: 'Estado', icon: CheckCircle },
  ];

  return (
    <section className="relative h-screen min-h-[780px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="images/Sierra_del_Tigre.jpeg"
          alt="Paisaje Sierra del Tigre"
          className="w-full h-full object-cover"
          style={{
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-slate-950/10" />
      </div>

      <div className="relative z-10 h-full flex items-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6">
                <StatusBadge />
              </div>

              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  Región Sierra del Tigre
                </span>
              </h1>

              <p className="text-xl text-gray-200 mb-8 max-w-xl">
                Estrategia integral para la gestión sustentable del territorio, equilibrando el desarrollo humano con la preservación de los ecosistemas de la región.
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToSection('introduccion')}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl transition-all"
                >
                  Explorar Región
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-4"
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <Icon className="mx-auto mb-2 text-cyan-200" size={24} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-xs text-white/70 tracking-wide">{stat.label}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <ArrowDown className="text-cyan-400" size={32} />
      </motion.div>
    </section>
  );
};

const SierradelTigre = () => {
  const [selectedMunicipality, setSelectedMunicipality] = useState(municipalities[0]);

  return (
    <div className="environmental-theme fade-in">
      <ModernBanner />

      <section id="introduccion" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 
              rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700"
          >
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="lg:w-2/3">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <Info className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Ordenamiento Ecológico Regional
                    <span className="block text-lg text-gray-600 dark:text-gray-300 mt-1">
                      Sierra del Tigre
                    </span>
                  </h2>
                </div>

                <div className="space-y-4 text-gray-700 dark:text-gray-300">
                  <p>
                    El <strong>Gobierno del Estado de Jalisco</strong> impulsa el desarrollo sustentable 
                    en la región denominada <strong>Sierra del Tigre</strong>, integrada por los municipios de 
                    <strong> Mazamitla, La Manzanilla de la Paz, Concepción de Buenos Aires, Valle de Juárez, Quitupan y Tamazula de Gordiano</strong>,
                    reconocida como una zona estratégica por su valor ambiental y potencial de desarrollo regional.
                  </p>
                  <p>
                    Con este propósito, se estableció un esquema de coordinación entre el Estado y los municipios para la elaboración del Programa de Ordenamiento Ecológico y Territorial Regional, así como instrumentos complementarios de planeación urbana. Dicho proceso tiene como finalidad regular los usos del suelo, orientar el aprovechamiento sustentable de los recursos naturales bajo un enfoque de cuenca y atender las problemáticas socioambientales de la región.
                  </p>
                  <p>
                    Este instrumento sienta las bases para una planeación integral del territorio, promoviendo el equilibrio entre el desarrollo económico, la conservación ambiental y la reducción de desigualdades, en congruencia con la política ambiental vigente.
                  </p>
                  <p>
                    Actualmente el proceso de ordenamiento ecológico de la región se encuentra en <strong>estado de suspensión</strong>, a la espera de las condiciones necesarias para su reactivación y conclusión.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="DocumentosE" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-full -mr-20 -mt-20 opacity-50" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                  className="flex-shrink-0"
                >
                  <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg">
                    <FileText className="w-16 h-16 text-white" />
                  </div>
                </motion.div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-semibold tracking-widest">
                      DOCUMENTO OFICIAL
                    </span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                    Informe de Diagnóstico
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-2">
                    Estatus del Proceso de Ordenamiento Ecológico Regional
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Sierra del Tigre - Análisis completo y recomendaciones técnicas
                  </p>
                </div>
                <motion.a
                  href="https://siga.jalisco.gob.mx/ordenamiento/archivos/Tigre/Gestion/Reporte%20Siera%20del%20Tigre.pdf"
                  target="_blank"
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex-shrink-0 relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl blur-lg opacity-75 group-hover:opacity-100 transition-all duration-300" />
                  <button className="relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700
                    text-white rounded-xl font-semibold flex items-center gap-2 shadow-lg hover:shadow-2xl transition-all duration-300">
                    <Download className="w-5 h-5" />
                    <span>Descargar PDF</span>
                    <motion.div
                      animate={{ y: [0, 5, 0] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </motion.div>
                  </button>
                </motion.a>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* <section id="Descargas" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <Descargas dataItems={Items} />
      </section> */}

      <section className="municipalities-section py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-gray-900 dark:to-gray-800 min-h-screen font-sans">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 dark:text-teal-400 text-center mb-16 relative">
            <MapPin className="inline-block mr-3 mb-1 w-10 h-10" />
            Municipios de la Región: Sierra del Tigre
            <p className="text-lg font-normal text-gray-600 dark:text-gray-400 mt-2">
              Conoce los municipios y sus características clave.
            </p>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {municipalities.map((municipality) => (
              <div
                key={municipality.id}
                onClick={() => setSelectedMunicipality(municipality)}
                className={`
                  municipality-card rounded-2xl overflow-hidden shadow-xl transition-all duration-500 cursor-pointer 
                  hover:shadow-2xl hover:scale-[1.03] bg-white/90 dark:bg-gray-800/80 backdrop-blur-sm 
                  border border-gray-100 dark:border-gray-700
                  ${selectedMunicipality.id === municipality.id 
                    ? 'ring-4 ring-teal-500 scale-[1.03] shadow-teal-300/50 dark:shadow-teal-700/50' 
                    : 'hover:ring-2 hover:ring-teal-300'
                  }
                `}
              >
                <div className={`h-28 ${municipality.color} flex flex-col items-center justify-center p-3 text-white font-bold`}>
                  <img
                    src={municipality.shield}
                    alt={`Escudo de ${municipality.name}`}
                    className="h-14 w-14 object-contain mb-1 drop-shadow-md"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/000000/ffffff?text=E"; }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-extrabold text-center text-gray-900 dark:text-white">{municipality.name}</h3>
                  <p className="text-xs text-center text-teal-600 dark:text-teal-400 mt-1 flex items-center justify-center">
                    Ver Detalles <ChevronRight className='w-3 h-3 ml-1'/>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div
            key={selectedMunicipality.id}
            className="municipality-details bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 max-w-6xl mx-auto border border-teal-200 dark:border-teal-900 animate-fade-in-up"
            style={{ '--delay': '200ms' }}
          >
            <div className="flex flex-col md:flex-row items-start gap-10">
              <div className="w-full md:w-1/3 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <img
                  src={selectedMunicipality.shield}
                  alt={`Escudo de ${selectedMunicipality.name}`}
                  className="h-48 w-48 object-contain shadow-lg rounded-full p-2 bg-white dark:bg-gray-900 mb-6 border-4 border-teal-300 dark:border-teal-500"
                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x192/000000/ffffff?text=E"; }}
                />
                <h3 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white text-center">{selectedMunicipality.name}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm text-center italic mt-2">{selectedMunicipality.description}</p>
              </div>

              <div className="w-full md:w-2/3">
                <h4 className="text-2xl font-bold text-teal-700 dark:text-teal-400 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center">
                  <Info className='w-5 h-5 mr-2'/> Datos Clave
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailBadge icon={Users} label="Población" value={selectedMunicipality.population} />
                  <DetailBadge icon={Map} label="Superficie" value={selectedMunicipality.area} />
                  <DetailBadge icon={Calendar} label="Municipalización" value={selectedMunicipality.foundation} />
                  <DetailBadge icon={Landmark} label="Región Administrativa" value={selectedMunicipality.administrativeRegion} />
                  <DetailBadge icon={MountainSnow} label="Zonas de Importancia Ecológica" value={selectedMunicipality.EcologicaZona} />
                </div>

                <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                    <TreePineIcon className='text-blue-500 mr-2 w-5 h-5'/> Principales Atracciones
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                    {selectedMunicipality.attractions}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="flex-1 relative bg-gray-200">
        <FullWidthVisorWithSidebar />
      </section> */}

      <ModernBitacoraSection items={bitacoraItems} />

      <FasesSection
        imageUrl="images/FasesOrdenamiento.png"
        altText="Fases del Ordenamiento Sierra del Tigre"
      />
    </div>
  );
};

export default SierradelTigre;
